import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, Download, DollarSign, Users, Building2, Printer, CheckCircle, AlertCircle, Landmark } from "lucide-react";
import { jsPDF } from "jspdf";

const API = '/api';

export default function Reports() {
  const { getAuthHeader, isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events?t=${Date.now()}`);
      setEvents(res.data);
    } catch (error) {
      toast.error("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const url = selectedEvent 
        ? `${API}/reports/totals-due?event_id=${selectedEvent}&t=${Date.now()}`
        : `${API}/reports/totals-due?t=${Date.now()}`;
      const res = await axios.get(url, {
        headers: getAuthHeader(),
      });
      setReport(res.data);
    } catch (error) {
      toast.error("Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const downloadPDF = () => {
    if (!report) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    // Title
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("NMLRA Trap Tracker", pageWidth / 2, y, { align: "center" });
    y += 10;

    doc.setFontSize(16);
    doc.text("Financial Report", pageWidth / 2, y, { align: "center" });
    y += 10;

    // Event filter
    if (selectedEvent) {
      const eventName = events.find(e => e.id === selectedEvent)?.name || "Unknown";
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Event: ${eventName}`, pageWidth / 2, y, { align: "center" });
      y += 8;
    }

    // Generated date
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date(report.generated_at).toLocaleString()}`, pageWidth / 2, y, { align: "center" });
    y += 15;

    // Summary Section
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Payment Summary", 14, y);
    y += 10;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`Total Collected: $${(report.total_collected || 0).toFixed(2)}`, 14, y);
    y += 6;
    doc.text(`Total Outstanding: $${(report.total_outstanding || 0).toFixed(2)}`, 14, y);
    y += 6;
    doc.text(`Total Sponsor Paid: $${(report.total_sponsor_paid || 0).toFixed(2)}`, 14, y);
    y += 8;
    doc.setFont("helvetica", "bold");
    doc.text(`Grand Total: $${(report.grand_total || 0).toFixed(2)}`, 14, y);
    y += 15;

    // Outstanding Payments Section
    if (report.outstanding && report.outstanding.shooters && report.outstanding.shooters.length > 0) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Outstanding Payments", 14, y);
      y += 8;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      
      report.outstanding.shooters.forEach((shooter) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        
        doc.setFont("helvetica", "bold");
        doc.text(`${shooter.shooter_name}`, 14, y);
        doc.text(`$${shooter.total_due.toFixed(2)}`, pageWidth - 14, y, { align: "right" });
        y += 6;
        
        doc.setFont("helvetica", "normal");
        shooter.rounds.forEach((round) => {
          doc.text(`  - Round ${round.round_number} (${round.match_name}): $${round.fee_amount.toFixed(2)}`, 18, y);
          y += 5;
        });
        y += 3;
      });

      doc.setFont("helvetica", "bold");
      doc.text(`Total Outstanding: $${(report.total_outstanding || 0).toFixed(2)}`, 14, y);
      y += 15;
    }

    // Sponsor Paid Section
    if (report.sponsor_paid && report.sponsor_paid.sponsors && report.sponsor_paid.sponsors.length > 0) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Sponsor Payments", 14, y);
      y += 8;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      
      report.sponsor_paid.sponsors.forEach((sponsor) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        
        doc.setFont("helvetica", "bold");
        doc.text(`${sponsor.sponsor_name}`, 14, y);
        doc.text(`$${sponsor.total_paid.toFixed(2)}`, pageWidth - 14, y, { align: "right" });
        y += 6;
        
        doc.setFont("helvetica", "normal");
        sponsor.rounds.forEach((round) => {
          doc.text(`  - ${round.shooter_name} (${round.match_name}): $${round.fee_amount.toFixed(2)}`, 18, y);
          y += 5;
        });
        y += 3;
      });

      doc.setFont("helvetica", "bold");
      doc.text(`Total Sponsor Paid: $${(report.total_sponsor_paid || 0).toFixed(2)}`, 14, y);
      y += 15;
    }

    // Tabs Section (Legacy)
    if (report.tabs && report.tabs.shooters && report.tabs.shooters.length > 0) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Shooter Tabs (Unpaid Entries)", 14, y);
      y += 8;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      
      report.tabs.shooters.forEach((shooter) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        
        doc.setFont("helvetica", "bold");
        doc.text(`${shooter.shooter_name}`, 14, y);
        doc.text(`$${shooter.total_due.toFixed(2)}`, pageWidth - 14, y, { align: "right" });
        y += 6;
        
        doc.setFont("helvetica", "normal");
        shooter.entries.forEach((entry) => {
          doc.text(`  - ${entry.event_name} / ${entry.match_name}: $${entry.entry_fee.toFixed(2)}`, 18, y);
          y += 5;
        });
        y += 3;
      });

      doc.setFont("helvetica", "bold");
      doc.text(`Total Tab Amount Due: $${(report.tabs.total_due || 0).toFixed(2)}`, 14, y);
      y += 15;
    }

    // Footer
    if (y > 260) {
      doc.addPage();
      y = 20;
    }
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setDrawColor(0);
    doc.line(14, y, pageWidth - 14, y);
    y += 8;
    doc.text(`National Muzzle Loading Rifle Association | ${new Date().toLocaleDateString()}`, pageWidth / 2, y, { align: "center" });

    // Save
    const eventName = selectedEvent 
      ? events.find(e => e.id === selectedEvent)?.name?.replace(/\s+/g, '_') || 'event'
      : 'all_events';
    doc.save(`financial_report_${eventName}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("PDF downloaded!");
  };

  const printReport = () => {
    window.print();
  };

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">Financial reports are only available to administrators.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="reports-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Financial Reports
          </h1>
          <p className="text-slate-500 mt-1">Generate reports for outstanding payments</p>
        </div>
      </div>

      {/* Report Generator */}
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <FileText className="text-[#1B3B6F]" />
            Totals Due Report
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">Filter by Event (optional)</label>
              <Select value={selectedEvent || "all"} onValueChange={(val) => setSelectedEvent(val === "all" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={generateReport}
                disabled={generating}
                className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                data-testid="generate-report-btn"
              >
                {generating ? "Generating..." : "Generate Report"}
              </Button>
            </div>
          </div>

          <p className="text-sm text-slate-500">
            This report shows all outstanding payments from shooters on tabs and pending sponsor payments.
          </p>
        </CardContent>
      </Card>

      {/* Report Display */}
      {report && (
        <div className="space-y-6 print:space-y-4" id="report-content">
          {/* Action Buttons */}
          <div className="flex gap-2 print:hidden">
            <Button onClick={downloadPDF} className="bg-green-700 hover:bg-green-600">
              <Download size={16} className="mr-2" />
              Download PDF
            </Button>
            <Button onClick={printReport} variant="outline">
              <Printer size={16} className="mr-2" />
              Print
            </Button>
          </div>

          {/* Main Summary Cards - Collected, Outstanding, Sponsor Paid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <CheckCircle className="mx-auto text-green-600 mb-2" size={24} />
                <p className="text-sm text-green-700 uppercase tracking-wider">Collected</p>
                <p className="font-mono text-2xl font-bold text-green-800" data-testid="collected-total">
                  ${(report.total_collected || 0).toFixed(2)}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Fees paid by shooters
                </p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4 text-center">
                <AlertCircle className="mx-auto text-red-600 mb-2" size={24} />
                <p className="text-sm text-red-700 uppercase tracking-wider">Outstanding</p>
                <p className="font-mono text-2xl font-bold text-red-800" data-testid="outstanding-total">
                  ${(report.total_outstanding || 0).toFixed(2)}
                </p>
                <p className="text-xs text-red-600 mt-1">
                  {report.outstanding?.total_rounds || 0} unpaid rounds
                </p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4 text-center">
                <Landmark className="mx-auto text-purple-600 mb-2" size={24} />
                <p className="text-sm text-purple-700 uppercase tracking-wider">Sponsor Paid</p>
                <p className="font-mono text-2xl font-bold text-purple-800" data-testid="sponsor-paid-total">
                  ${(report.total_sponsor_paid || 0).toFixed(2)}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  {report.sponsor_paid?.total_sponsors || 0} sponsor(s)
                </p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 text-center">
                <DollarSign className="mx-auto text-blue-600 mb-2" size={24} />
                <p className="text-sm text-blue-700 uppercase tracking-wider">Grand Total</p>
                <p className="font-mono text-2xl font-bold text-blue-800" data-testid="grand-total">
                  ${(report.grand_total || 0).toFixed(2)}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {report.total_rounds || 0} total rounds
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Outstanding Rounds Detail */}
          {report.outstanding && report.outstanding.shooters && report.outstanding.shooters.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <AlertCircle className="text-red-600" />
                  Outstanding Payments ({report.outstanding.total_shooters} shooters)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {report.outstanding.shooters.map((shooter) => (
                    <div key={shooter.shooter_id} className="border rounded-lg p-4 bg-red-50/50">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">{shooter.shooter_name}</span>
                        <span className="font-mono font-bold text-red-700">
                          ${shooter.total_due.toFixed(2)}
                        </span>
                      </div>
                      <div className="text-sm text-slate-600 space-y-1">
                        {shooter.rounds.map((round, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span>Round {round.round_number} - {round.match_name}</span>
                            <span className="font-mono">${round.fee_amount.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sponsor Paid Detail */}
          {report.sponsor_paid && report.sponsor_paid.sponsors && report.sponsor_paid.sponsors.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <Landmark className="text-purple-600" />
                  Sponsor Payments ({report.sponsor_paid.total_sponsors} sponsors)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {report.sponsor_paid.sponsors.map((sponsor) => (
                    <div key={sponsor.sponsor_id} className="border rounded-lg p-4 bg-purple-50/50">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">{sponsor.sponsor_name}</span>
                        <span className="font-mono font-bold text-purple-700">
                          ${sponsor.total_paid.toFixed(2)}
                        </span>
                      </div>
                      <div className="text-sm text-slate-600 space-y-1">
                        {sponsor.rounds.map((round, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span>{round.shooter_name} - {round.match_name}</span>
                            <span className="font-mono">${round.fee_amount.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Legacy: Tabs Section */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <Users className="text-orange-600" />
                Shooter Tabs ({report.tabs?.total_shooters || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!report.tabs?.shooters || report.tabs.shooters.length === 0) ? (
                <p className="text-slate-500 text-center py-4">No outstanding tabs</p>
              ) : (
                <div className="space-y-4">
                  {report.tabs.shooters.map((shooter) => (
                    <div key={shooter.shooter_id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">{shooter.shooter_name}</span>
                        <span className="font-mono font-bold text-orange-700">
                          ${shooter.total_due.toFixed(2)}
                        </span>
                      </div>
                      <div className="text-sm text-slate-600 space-y-1">
                        {shooter.entries.map((entry, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span>{entry.event_name} / {entry.match_name}</span>
                            <span className="font-mono">${entry.entry_fee.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Legacy: Sponsor Pending */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <Building2 className="text-purple-600" />
                Sponsor Pending Approval ({report.sponsors?.total_sponsors || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!report.sponsors?.sponsors || report.sponsors.sponsors.length === 0) ? (
                <p className="text-slate-500 text-center py-4">No pending sponsor payments</p>
              ) : (
                <div className="space-y-4">
                  {report.sponsors.sponsors.map((sponsor) => (
                    <div key={sponsor.sponsor_id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">{sponsor.sponsor_name}</span>
                        <span className="font-mono font-bold text-purple-700">
                          ${sponsor.total_due.toFixed(2)}
                        </span>
                      </div>
                      <div className="text-sm text-slate-600 space-y-1">
                        {sponsor.entries.map((entry, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span>{entry.shooter_name} ({entry.event_name} / {entry.match_name})</span>
                            <span className="font-mono">${entry.entry_fee.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
